import {MOVEMENT,worldColliders} from './movement.mjs';
import {transformPortalPoint,transformPortalVector} from './portal-math.mjs';
const frameOf=p=>({id:p.id??null,accountId:p.accountId??null,worldId:p.worldId,crossingSeq:p.crossingSeq??0});
const sameFrame=(a,b)=>(a.id??null)===(b.id??null)&&(a.accountId??null)===(b.accountId??null)&&a.worldId===b.worldId&&(a.crossingSeq??0)===(b.crossingSeq??0);
const resting=p=>p.grounded===true&&!p.flying&&Math.hypot(p.velocityX??0,p.velocityZ??0,p.velocityY??0)<1e-8;
const finitePosition=p=>p&&['x','y','z'].every(axis=>Number.isFinite(p[axis]));
const simulationFields=['velocityX','velocityZ','velocityY','grounded','flying','lastFlightToggleSeq','jumpHeld','coyoteRemaining','jumpBufferRemaining','portalCooldownRemaining'];

/** Socket input acknowledgements are held-control edges, not replay timestamps.
 * Reconcile in the native frame clock: a packet may update the desired error,
 * but cannot move the eye/body between frames or rewind a completed doorway.
 * Explicit frame changes still belong to portal/invitation/reset handlers. */
export function createSocketMovementReconciliation() {
  let neutral=null,frame=null,lastSnapshotTime=-Infinity,correction=null;
  return {
    reset(){neutral=null;frame=null;lastSnapshotTime=-Infinity;correction=null;},
    crossed(authority){
      neutral=null;frame=frameOf(authority);correction=null;
      lastSnapshotTime=Number.isFinite(authority.lastCrossing?.time)?authority.lastCrossing.time:-Infinity;
    },
    record(input,player){
      if(!frame||!sameFrame(frame,player)) {
        frame=frameOf(player);
        neutral=null;lastSnapshotTime=-Infinity;correction=null;
      }
      if(input.moveX||input.moveZ||input.moveY||input.jump)neutral=null;
      else if(neutral===null)neutral=input.seq;
    },
    reconcile(player,authority,time){
      if(!sameFrame(player,authority)||!finitePosition(authority.position))return player;
      if(Number.isFinite(time)) {
        if(time<=lastSnapshotTime)return player;
        lastSnapshotTime=time;
      }
      const a=player.position,b=authority.position;
      if(!finitePosition(a)){correction=null;return {...player,...authority,position:{...b}};}
      const acknowledgedRest=neutral!==null&&(authority.lastInputSeq??0)>=neutral&&resting(player)&&resting(authority);
      // Preserve the established neutral-edge settlement: once both sides rest,
      // accept the exact authority once instead of introducing idle creeping.
      if(acknowledgedRest){correction=null;return {...player,...authority,position:{...b}};}
      correction={...frameOf(player),x:b.x-a.x,y:b.y-a.y,z:b.z-a.z};
      const result={...player,avatar:authority.avatar};
      if((authority.lastInputSeq??0)>=(player.lastInputSeq??0)) {
        for(const field of simulationFields)if(authority[field]!==undefined)result[field]=authority[field];
      }
      return result;
    },
    advance(player,dt,world,{suspended=false,presentation=null,worlds=null}={}){
      if(suspended){correction=null;return player;}
      if(!correction)return player;
      if(!sameFrame(correction,player)){correction=null;return player;}
      const elapsed=Math.min(.055,Math.max(0,Number.isFinite(dt)?dt:0));
      if(!elapsed)return player;
      const remaining=Math.hypot(correction.x,correction.y,correction.z);
      if(remaining<1e-8){correction=null;return player;}
      // Same ordinary 8%/15-Hz convergence, distributed across native frames.
      // A delayed packet cannot overpower currently held native locomotion.
      const speed=Math.hypot(player.velocityX??0,player.velocityY??0,player.velocityZ??0);
      const gain=1-Math.exp(-elapsed*(-Math.log(.92)*15));
      const limit=Math.max(MOVEMENT.speed,speed)*.35*elapsed;
      let fraction=Math.min(gain,limit/remaining);
      if(world) {
        let start=player.position,shift={x:correction.x*fraction,y:correction.y*fraction,z:correction.z*fraction},physicalWorld=world;
        if(presentation) {
          physicalWorld=worlds instanceof Map?worlds.get(presentation.targetWorldId):worlds?.[presentation.targetWorldId];
          if(presentation.sourceWorldId!==world.id||!physicalWorld){correction=null;return player;}
          start=transformPortalPoint(start,presentation.source,presentation.target,presentation.sourceSide,presentation.targetSide);
          shift=transformPortalVector(shift,presentation.source,presentation.target,presentation.sourceSide,presentation.targetSide);
        }
        fraction*=correctionClearance(start,shift,worldColliders(physicalWorld));
      }
      const shift={x:correction.x*fraction,y:correction.y*fraction,z:correction.z*fraction};
      correction.x-=shift.x;correction.y-=shift.y;correction.z-=shift.z;
      return {...player,position:{x:player.position.x+shift.x,y:player.position.y+shift.y,z:player.position.z+shift.z}};
    },
  };
}

// Reconciliation uses the same native body envelope as movement. A packet
// correction cannot shift the starting capsule into a wall before native input
// collision resolution gets a chance to see the segment.
function correctionClearance(start,shift,colliders) {
  let allowed=1;
  const distance=Math.hypot(shift.x,shift.y,shift.z);
  if(distance<1e-12)return allowed;
  for(const box of colliders) {
    let near=0,far=1,inside=true;
    for(const axis of ['x','y','z']) {
      const min=axis==='y'?(box.minY??0)-MOVEMENT.height:box[axis==='x'?'minX':'minZ']-MOVEMENT.radius;
      const max=axis==='y'?(box.maxY??100):box[axis==='x'?'maxX':'maxZ']+MOVEMENT.radius;
      const value=start[axis],delta=shift[axis];
      if(value<=min+.000001||value>=max-.000001)inside=false;
      if(Math.abs(delta)<1e-12){if(value<=min+.000001||value>=max-.000001){near=2;break;}}
      else {const first=(min-value)/delta,second=(max-value)/delta;near=Math.max(near,Math.min(first,second));far=Math.min(far,Math.max(first,second));}
    }
    if(!inside&&near<far&&far>0&&near<allowed)allowed=Math.max(0,near-.001/distance);
  }
  return allowed;
}
